import { useEffect, useState } from "react";
import { api, dateFmt, formatApiError } from "@/lib/api";
import { Sparkles, Phone, Mail, ArrowRight, Trash2, MessageCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

const STATUS = [
    { v: "lead", l: "Lead nuevo", c: "fm-badge-warning" },
    { v: "contactado", l: "Contactado", c: "fm-badge-neutral" },
    { v: "ganado", l: "Convertido", c: "fm-badge-positive" },
    { v: "perdido", l: "Perdido", c: "fm-badge-critical" },
];

export default function Prospects() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        try { const r = await api.get("/prospects"); setList(r.data || []); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const filtered = filter === "all" ? list : list.filter(p => p.status === filter);

    const setStatus = async (p, st) => {
        try { await api.put(`/prospects/${p.id}`, { status: st }); toast.success("Estatus actualizado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const convert = async (p) => {
        if (!window.confirm(`¿Convertir "${p.name}" en cliente registrado?\n\nSus cotizaciones previas se vincularán al nuevo cliente.`)) return;
        try {
            const r = await api.post(`/prospects/${p.id}/convert-to-client`, {
                type: "particular", business_name: p.name || "Cliente",
            });
            if (r.data?.already) toast.info("Ya estaba convertido");
            else toast.success(`Cliente creado: ${r.data.client.business_name}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const remove = async (p) => {
        if (!window.confirm(`¿Eliminar prospecto "${p.name}"?\nLas cotizaciones se conservan.`)) return;
        try { await api.delete(`/prospects/${p.id}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const counts = STATUS.reduce((acc, s) => ({ ...acc, [s.v]: list.filter(p => p.status === s.v).length }), {});

    return (
        <div className="space-y-4" data-testid="prospects-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-amber-500" /> Prospectos (CRM)
                        <HelpTip text="Cada cotización rápida generada en la calle queda aquí. Dale seguimiento, conviértelo en cliente, o márcalo como perdido. Es tu pipeline comercial." />
                    </h2>
                    <div className="text-xs text-slate-500 mt-1 font-mono">{list.length} prospectos · {counts.ganado || 0} convertidos · {counts.lead || 0} leads frescos</div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider ${filter === "all" ? "bg-red-600 text-white" : "bg-slate-100"}`}>Todos ({list.length})</button>
                {STATUS.map(s => (
                    <button key={s.v} onClick={() => setFilter(s.v)} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider ${filter === s.v ? "bg-red-600 text-white" : "bg-slate-100"}`} data-testid={`filter-${s.v}`}>
                        {s.l} ({counts[s.v] || 0})
                    </button>
                ))}
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Nombre", "Contacto", "Origen", "Cotizaciones", "Última", "Estatus", "Acciones"].map(h => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {filtered.map(p => {
                            const stCfg = STATUS.find(s => s.v === p.status) || STATUS[0];
                            return (
                                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 font-medium">{p.name || "—"}</td>
                                    <td className="py-2 px-3 text-xs">
                                        {p.phone && <div className="flex items-center gap-1 font-mono"><Phone className="w-3 h-3 text-slate-400" /> {p.phone}</div>}
                                        {p.email && <div className="flex items-center gap-1 font-mono text-[11px]"><Mail className="w-3 h-3 text-slate-400" /> {p.email}</div>}
                                    </td>
                                    <td className="py-2 px-3 text-xs uppercase tracking-wider text-slate-500">{p.source || "—"}</td>
                                    <td className="py-2 px-3 text-center font-mono">{p.quote_count || 0}</td>
                                    <td className="py-2 px-3 text-[11px] font-mono">{p.last_quote_folio || "—"}<br /><span className="text-slate-400">{dateFmt(p.last_quote_at)}</span></td>
                                    <td className="py-2 px-3">
                                        <select value={p.status} onChange={(e) => setStatus(p, e.target.value)} className={`fm-badge cursor-pointer ${stCfg.c} border-none`} data-testid={`status-${p.id}`}>
                                            {STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                                        </select>
                                    </td>
                                    <td className="py-2 px-3">
                                        <div className="flex items-center gap-2 text-xs">
                                            {p.phone && !p.converted_client_id && (
                                                <a href={`https://wa.me/${p.phone.replace(/[^0-9+]/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" title="Contactar por WhatsApp">
                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                            {!p.converted_client_id && (
                                                <button onClick={() => convert(p)} className="text-purple-700 hover:text-purple-900 inline-flex items-center gap-1" title="Convertir en cliente" data-testid={`convert-${p.id}`}>
                                                    <UserPlus className="w-3.5 h-3.5" /> Convertir
                                                </button>
                                            )}
                                            {p.converted_client_id && <span className="text-emerald-600 text-[11px]">✓ Cliente</span>}
                                            <button onClick={() => remove(p)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filtered.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <Sparkles className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin prospectos. Cuando la tripulación genere cotizaciones rápidas, aparecerán aquí.
                    </div>
                )}
            </div>
        </div>
    );
}
